import { Injectable } from '@angular/core';
import { McticHttpClient, McticHttpParams, Page, Pageable } from '@mctic/ng-core';
import { Observable } from 'rxjs';
import { AnoBase, ConfiguracaoAnoBase, Identity } from '..';
import * as moment from 'moment';
import { ConfiguracaoAnoBaseVO } from '../shared/model/configuracao-ano-base-vo.model';
import { TipoConfiguracaoAnoBaseEnum } from '../shared/enum/tipo-configuracao-ano-base.enum';
import { publishReplay, refCount } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class ConfiguracaoAnoBaseService {

  anoBaseSelecionado: AnoBase;
  configuracaoAnoBaseVO: ConfiguracaoAnoBaseVO;
  visualizar: boolean = false;

  private _configuracaoCartaCredito$: Observable<ConfiguracaoAnoBase>;
  private _configuracoesDatas$: Observable<ConfiguracaoAnoBaseVO>;
  private _configuracaoRetificaCartaCredito$: Observable<ConfiguracaoAnoBase>;
  private _configuracaoAuditoria$: Observable<ConfiguracaoAnoBase>;
  private _configuracaoAuditoriaPadis$: Observable<ConfiguracaoAnoBase>;
  private _configuracaoAuditoriaSuspensao$: Observable<ConfiguracaoAnoBase>;
  private readonly endpoint = '/sepin-backend/rest/configuracoes-ano-base';

  constructor(private readonly http: McticHttpClient) {
  }

  obterConfiguracaoAnoBasePorIdAnoBase(idAnoBase: number):
    Observable<ConfiguracaoAnoBaseVO> {

    if (!this._configuracoesDatas$) {
      this._configuracoesDatas$ = this.http.get<ConfiguracaoAnoBaseVO>(
        `${this.endpoint}/ano-base/${idAnoBase}`).pipe(
          publishReplay(1), refCount()
        );
    }
    return this._configuracoesDatas$;
  }

  obterConfiguracoesAnoBase(): Observable<ConfiguracaoAnoBase[]> {
    return this.http.get<ConfiguracaoAnoBase[]>(this.endpoint);
  }

  obterConfiguracaoAnoBasePorTipo(idTipoConfiguracao: TipoConfiguracaoAnoBaseEnum): Observable<ConfiguracaoAnoBase> {
    return this.http.get<ConfiguracaoAnoBase>(`${this.endpoint}/tipo/${idTipoConfiguracao}`);
  }

  obterConfiguracaoCartaCredito(): Observable<ConfiguracaoAnoBase> {
    if (!this._configuracaoCartaCredito$) {
      this._configuracaoCartaCredito$ = this.obterConfiguracaoAnoBasePorTipo(TipoConfiguracaoAnoBaseEnum.CartaCredito).pipe(
        publishReplay(1), refCount()
      );
    }

    return this._configuracaoCartaCredito$;
  }

  obterConfiguracaoRetificaCartaCredito(): Observable<ConfiguracaoAnoBase> {
    if (!this._configuracaoRetificaCartaCredito$) {
      this._configuracaoRetificaCartaCredito$ = this.obterConfiguracaoAnoBasePorTipo(TipoConfiguracaoAnoBaseEnum.RetificacaoCartaCredito).pipe(
        publishReplay(1), refCount()
      );
    }

    return this._configuracaoRetificaCartaCredito$;
  }

  obterConfiguracaoAuditoria(): Observable<ConfiguracaoAnoBase> {
    if (!this._configuracaoAuditoria$) {
      this._configuracaoAuditoria$ = this.obterConfiguracaoAnoBasePorTipo(
        TipoConfiguracaoAnoBaseEnum.Auditoria).pipe(
          publishReplay(1), refCount()
        );
    }
    return this._configuracaoAuditoria$;
  }

  obterConfiguracaoAuditoriaPadis(): Observable<ConfiguracaoAnoBase> {
    if (!this._configuracaoAuditoriaPadis$) {
      this._configuracaoAuditoriaPadis$ = this.obterConfiguracaoAnoBasePorTipo(
        TipoConfiguracaoAnoBaseEnum.AuditoriaPadis).pipe(
          publishReplay(1), refCount()
        );
    }
    return this._configuracaoAuditoriaPadis$;
  }

  obterConfiguracaoAuditoriaSuspensao(): Observable<ConfiguracaoAnoBase> {
    if (!this._configuracaoAuditoriaSuspensao$) {
      this._configuracaoAuditoriaSuspensao$ = this.obterConfiguracaoAnoBasePorTipo(
        TipoConfiguracaoAnoBaseEnum.AuditoriaSuspensao).pipe(
          publishReplay(1), refCount()
        );
    }
    return this._configuracaoAuditoriaSuspensao$;
  }

  limparCacheCartaCredito() {
    this._configuracaoCartaCredito$ = null;
  }

  limparCacheAuditoria() {
    this._configuracaoAuditoria$ = null;
  }

  limparCacheAuditoriaSuspensao() {
    this._configuracaoAuditoriaSuspensao$ = null;
  }

  excluirConfiguracao(idAnoBase: number) {
    return this.http.delete(`${this.endpoint}/${idAnoBase}`);
  }

  validarPeriodo(idAnoBase: number): Observable<any> {
    return this.http.get<any>(`${this.endpoint}/valido/${idAnoBase}`);
  }

  consultar(pageable: Pageable): Observable<Page<ConfiguracaoAnoBase>> {
    const queryParams = new McticHttpParams().setPageable(pageable);
    return this.http.get<Page<ConfiguracaoAnoBase>>(`${this.endpoint}/query`, {
      params: queryParams.toHttpParams()
    });
  }

  salvar(
    configuracaoAnoBaseVO: ConfiguracaoAnoBaseVO
  ): Observable<Identity<number>> {
    return this.http.post<Identity<number>>(
      this.endpoint,
      configuracaoAnoBaseVO
    );
  }

  alterar(configuracaoAnoBaseVO: ConfiguracaoAnoBaseVO) {
    return this.http.put(this.endpoint, configuracaoAnoBaseVO);
  }

  isPeriodo(configuracaoAnoBase: ConfiguracaoAnoBase): boolean {
    if (configuracaoAnoBase != undefined) {
      let dataAtual = moment(configuracaoAnoBase.dataAtual);
      return (
        dataAtual.isSameOrAfter(configuracaoAnoBase.dataInicio) &&
        dataAtual.isSameOrBefore(configuracaoAnoBase.dataFim)
      );
    }
    return false;
  }

  isPeriodoPreenchimentoRda(configuracoesAnoBase: ConfiguracaoAnoBase[]) {
    const configuracaoEntregaRda = this.obterConfiguracao(
      configuracoesAnoBase,
      TipoConfiguracaoAnoBaseEnum.EntregaRda
    );
    const configuracaoRetificacao = this.obterConfiguracao(
      configuracoesAnoBase,
      TipoConfiguracaoAnoBaseEnum.Retificacao
    );
    const configuracaoPeriodoSuspensao = this.obterConfiguracao(
      configuracoesAnoBase,
      TipoConfiguracaoAnoBaseEnum.EntregaRdaSuspensao
    );
    const configuracaoPeriodoRdaPadis = this.obterConfiguracao(
      configuracoesAnoBase,
      TipoConfiguracaoAnoBaseEnum.RdaPadis
    );
    const configuracaoPeriodoRdaPadisSuspensao = this.obterConfiguracao(
      configuracoesAnoBase,
      TipoConfiguracaoAnoBaseEnum.EntregaRdaPadisSuspensao
    );

    return (this.isPeriodo(configuracaoEntregaRda) ||
      this.isPeriodo(configuracaoRetificacao) ||
      this.isPeriodo(configuracaoPeriodoRdaPadis) ||
      this.isPeriodo(configuracaoPeriodoSuspensao) ||
      this.isPeriodo(configuracaoPeriodoRdaPadisSuspensao)
    );
  }

  obterConfiguracao(configuracoesAnoBase: ConfiguracaoAnoBase[],tipoConfiguracao: TipoConfiguracaoAnoBaseEnum): ConfiguracaoAnoBase {
    return configuracoesAnoBase.find(configuracao => configuracao.tipoConfiguracao.id === tipoConfiguracao);
  }

}
